package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.*
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.entityType
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.CloudEvent
import unifiedcontent.aggregator.CloudEventList
import unifiedcontent.aggregator.EventSearchStatus
import unifiedcontent.aggregator.EventSearchStatusState
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.jsonObject
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val ALL_ENTITY_TYPES_VALUE = "__ALL__"

private fun CloudEvent.producerEventEntityType(): String? {
    val eventData = data?.jsonObject ?: return null
    return when (type) {
        "Content Update" -> eventData.entityType
        "Content Update Partial" -> eventData["modification"]?.entityType
        else -> eventData.entityType
    }
}

/**
 * Displays valid (successful) producer events in a flat table with time range
 * selection, an event type filter (Add/Update/Remove), and an entity type filter
 * derived from the producer's allowed entity types. The caller provides a query
 * lambda that uses the per-producer events endpoint to fetch events server-side
 * filtered to the specific producer.
 *
 * The view is **controlled**: it holds no filter state of its own, and every filter change is handed
 * to [onFiltersChange]. That is what lets the producer detail page keep the selections in the URL
 * (UCA-1028 follow-up) while a call site that wants them ephemeral can back them with a plain
 * `remember`. Making it controlled rather than adding a "sync to URL" flag keeps the decision at the
 * call site, where it is visible, instead of inside a branch here.
 *
 * @param aggregator the aggregator service for API calls and entity lookups
 * @param allowedEntityTypes the entity type IDs this producer is permitted to publish,
 *   used to populate the entity type filter dropdown
 * @param filters the current filter selections. Only [EventFilters.range] triggers a fetch; the event
 *   type and entity type filters apply client-side to the loaded events.
 * @param onFiltersChange called with the new selections whenever the user changes a filter
 * @param query the API query lambda that fetches producer events for a time range or query id
 */
@Composable
internal fun ProducerValidEvents(
    aggregator: AggregatorService,
    allowedEntityTypes: List<String>,
    filters: EventFilters,
    onFiltersChange: (EventFilters) -> Unit,
    query: suspend (range: TimeRange?, queryId: String?) -> EventSearchStatus
) {
    var loading by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<EventSearchStatus?>(null) }
    var events by remember { mutableStateOf<CloudEventList?>(null) }
    var failureMessage by remember { mutableStateOf<String?>(null) }

    val eventsPeriod = filters.range
    val selectedEventType = filters.eventType

    // An entity type the producer is not permitted to publish can never match an event, so honouring
    // it would show an empty table with a filter the dropdown cannot even display. A URL naming a
    // stale or mistyped type therefore falls back to "All entity types" rather than to no results.
    val selectedEntityType = filters.entityType?.takeIf { it in allowedEntityTypes }

    Style(EntityEventsStyles)

    // Keyed on the range alone: the other two filters run client-side over the loaded events, so
    // changing them must not refetch. TimeRange and Period are data classes, so a range decoded from
    // the URL compares equal across recompositions and does not retrigger this on every navigation.
    LaunchedEffect(eventsPeriod) {
        val range = eventsPeriod?.range()
        if (eventsPeriod != null && eventsPeriod.isOutsideEventRetention()) {
            // A range from a link or bookmark can predate retention; don't send a search the
            // backend can only reject (UCA-1168). Reset loading here too: a range change cancels
            // an in-flight search, so the previous run may never have cleared it.
            loading = false
            result = null
            events = null
            failureMessage = EVENT_RETENTION_MESSAGE
        } else if (range != null) {
            loading = true
            failureMessage = null
            events = null
            try {
                result = loadAccumulatedEvents({ queryId ->
                    query(eventsPeriod, queryId)
                }) {
                    events = it
                }
                if (result?.state == EventSearchStatusState.FAILED) {
                    failureMessage = "We were unable to load the events"
                }
            } catch (e: CancellationException) {
                // A range change cancels the previous LaunchedEffect run mid-poll; that's a normal
                // supersession, not a failed search, and must propagate so the coroutine actually
                // stops rather than reporting a spurious error over the newer run's state.
                throw e
            } catch (t: Throwable) {
                failureMessage = t.message ?: "We were unable to load the events"
            } finally {
                // In finally so a cancelled run still clears it; otherwise a range change mid-poll
                // could leave "Loading events..." showing indefinitely.
                loading = false
            }
        }
    }

    // Header row with loading indicator, entity type filter, event type filter, and time range
    Div({
        classes(EntityEventsStyles.eventsHeader)
    }) {
        Div({
            classes(EntityEventsStyles.loadingBlock)
        }) {
            if (loading) {
                spinner()
                Text("Loading events...")
            }
        }

        // Entity type filter — only shown when the producer has multiple allowed entity types
        if (allowedEntityTypes.size > 1) {
            Div({ classes(EventTypeFilterStyles.container) }) {
                Div({ classes(EventTypeFilterStyles.trigger) }) {
                    val currentSelection = selectedEntityType ?: ALL_ENTITY_TYPES_VALUE
                    val options = listOf("All entity types" to ALL_ENTITY_TYPES_VALUE) +
                        allowedEntityTypes.map { type ->
                            val displayName = aggregator.entityType(type)?.name ?: type
                            displayName to type
                        }

                    simpleSelect(currentSelection, options) { selected ->
                        onFiltersChange(
                            filters.copy(
                                entityType = selected.takeIf { it != ALL_ENTITY_TYPES_VALUE }
                            )
                        )
                    }
                }
            }
        }

        Div {
            EventTypeFilterControl(selectedEventType) { updated ->
                onFiltersChange(filters.copy(eventType = updated))
            }
        }

        Div {
            timeRangeSelect(
                defaultTimeRanges,
                selected = eventsPeriod,
                earliest = remember { eventRetentionCutoff() },
                note = EVENT_RETENTION_NOTE
            ) { range ->
                onFiltersChange(filters.copy(range = range))
            }
        }
    }

    val producerEvents = remember(events) {
        events.orEmpty()
    }
    val filteredByEventType = remember(producerEvents, selectedEventType) {
        producerEvents.filterByEventTypes(selectedEventType.asFilterSet())
    }
    val filteredEvents = remember(filteredByEventType, selectedEntityType) {
        if (selectedEntityType == null) {
            filteredByEventType
        } else {
            filteredByEventType.filter { event ->
                event.producerEventEntityType() == selectedEntityType
            }
        }
    }

    box({
        paddedContent = false
    }) {
        if (failureMessage != null) {
            boxMessage(failureMessage!!)
        } else if (eventsPeriod == null) {
            boxMessage("Please select a time period.")
        }

        if (producerEvents.isNotEmpty()) {
            if (filteredEvents.isNotEmpty()) {
                cloudEventTable(aggregator, filteredEvents)
            } else if (selectedEventType != null || selectedEntityType != null) {
                boxMessage("No events match the selected filters.")
            }
        } else {
            if (!loading && eventsPeriod != null && failureMessage == null) {
                boxMessage("No events occurred in this period.")
            }
        }
    }
}
